import { useState } from 'react';
import {
  Sparkles, ChevronDown, ChevronUp, TrendingUp, AlertTriangle, ShieldAlert, Info,
} from 'lucide-react';
import buildQualityNarrative from '@/lib/qualityNarrative';
import AbbrText from '@/components/AbbrText';

/**
 * QualityNarrativePanel — collapsible "Why this score?" panel that
 * sits at the top of the Stock Metrics tab in the Stock Modal. Same
 * design and tone language as the Triangle narrative panel so users
 * recognize the storytelling pattern across both score systems.
 *
 * Open by default; clicking the header collapses to a single-line
 * peek so power users browsing dozens of stocks can collapse it.
 *
 * Renders nothing when the stock has no quality data — falls back
 * gracefully on thinly cached / non-dividend tickers.
 */
const TONE_STYLE = {
  positive: 'border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/50 text-emerald-900 dark:text-emerald-100',
  caution: 'border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/30 dark:border-amber-800/50 text-amber-900 dark:text-amber-100',
  negative: 'border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 text-rose-900 dark:text-rose-100',
  info: 'border-sky-300/60 bg-sky-50/40 dark:bg-sky-950/20 dark:border-sky-800/50 text-sky-900 dark:text-sky-100',
};

const TONE_ICON = {
  positive: <TrendingUp className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />,
  caution: <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />,
  negative: <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />,
  info: <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />,
};

export default function QualityNarrativePanel({ stock }) {
  const [open, setOpen] = useState(true);
  const allItems = buildQualityNarrative(stock);
  // Round 86 (Feb 2026) — Filter out bullets that already render
  // prominently above the modal tabs (via `HeadlineInsight`):
  //   • `etf-explainer` — the ETF context bullet
  //   • `high-yield-trap` — the yield-trap warning bullet
  // Both still get picked by HeadlineInsight, so showing them again in
  // this panel was duplicate content.
  const items = (allItems || []).filter((b) => b?.key !== 'etf-explainer' && b?.key !== 'high-yield-trap');
  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card mb-3" data-testid="quality-why-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
        data-testid="quality-why-toggle"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" />
          Why this Quality Score?
          <span className="text-[10px] font-normal text-muted-foreground/70 normal-case tracking-normal">
            ({items.length} {items.length === 1 ? 'insight' : 'insights'})
          </span>
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      {open && (
        <ul className="px-3 pb-3 pt-1 space-y-1.5" data-testid="quality-why-list">
          {items.map((item, idx) => (
            <li
              key={idx}
              className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs leading-snug ${TONE_STYLE[item.tone] || TONE_STYLE.info}`}
              data-testid={`quality-why-item-${idx}`}
            >
              {TONE_ICON[item.tone] || TONE_ICON.info}
              <span><AbbrText text={item.text} as="span" /></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
